import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, ExternalLink, BookOpen } from "lucide-react";

export default function BookingPagesPage() {
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedWs, setSelectedWs] = useState<string>("");
  const [showCreate, setShowCreate] = useState(false);
  const [newPage, setNewPage] = useState({ title: "", description: "", slug: "", duration_minutes: 30, buffer_minutes: 10 });

  useEffect(() => {
    api.get("/api/v1/workspaces").then((ws) => {
      setWorkspaces(ws);
      if (ws.length > 0) setSelectedWs(ws[0].id.toString());
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedWs) return;
    api.get(`/api/v1/booking-pages?workspaceId=${selectedWs}`).then(setPages).catch(() => {});
  }, [selectedWs]);

  const handleCreate = async () => {
    if (!newPage.title || !newPage.slug || !selectedWs) return;
    try {
      const result = await api.post("/api/v1/booking-pages", { ...newPage, workspaceId: parseInt(selectedWs) });
      setPages([{ id: result.id, ...newPage }, ...pages]);
      setNewPage({ title: "", description: "", slug: "", duration_minutes: 30, buffer_minutes: 10 });
      setShowCreate(false);
      toast.success("Booking page created!");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this booking page?")) return;
    try { await api.delete(`/api/v1/booking-pages/${id}`); setPages(pages.filter(p => p.id !== id)); toast.success("Deleted"); }
    catch (e: any) { toast.error(e.message); }
  };

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    toast.success("Booking URL copied!");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Booking Pages</h1>
        <div className="flex items-center gap-3">
          {workspaces.length > 0 && (
            <select value={selectedWs} onChange={(e) => setSelectedWs(e.target.value)} className="px-3 py-2 rounded-lg border border-input bg-background text-sm">
              {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Page
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="bg-card rounded-xl border border-primary/30 p-5 mb-6">
          <h3 className="font-semibold mb-4">Create Booking Page</h3>
          <div className="grid gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Title</label>
              <input value={newPage.title} onChange={(e) => setNewPage({ ...newPage, title: e.target.value })} placeholder="30-min Meeting" className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Slug (URL path)</label>
              <input value={newPage.slug} onChange={(e) => setNewPage({ ...newPage, slug: e.target.value })} placeholder="30min" className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <textarea value={newPage.description} onChange={(e) => setNewPage({ ...newPage, description: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Duration (min)</label>
                <input type="number" value={newPage.duration_minutes} onChange={(e) => setNewPage({ ...newPage, duration_minutes: parseInt(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Buffer (min)</label>
                <input type="number" value={newPage.buffer_minutes} onChange={(e) => setNewPage({ ...newPage, buffer_minutes: parseInt(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">Create</button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-input text-sm hover:bg-accent">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">{[1,2].map(i=><div key={i} className="h-20 bg-muted rounded-xl"/>)}</div>
      ) : pages.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">No booking pages yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create a booking page to let contacts schedule meetings.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pages.map(p => (
            <div key={p.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.title}</p>
                <p className="text-xs text-muted-foreground">{p.duration_minutes}min · /book/{p.slug}</p>
              </div>
              <a href={`/book/${p.slug}`} target="_blank" className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => copyUrl(p.slug)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><Copy className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
